import { config } from '../config';
import { StorageError } from '../app/errors';
import { db } from './harnessDb';
import type { AudioChunkRow, AudioSegmentRow } from './harnessDb';

/**
 * Design §1.2 — `data/`: the only place `dexie` is imported. Design §8.3 / DD-2 — audio lives in
 * IndexedDB behind a small store interface, never in the Cache API or the file system.
 *
 * BUILD-UNIT NOTE (U-1, amended by U-3): this was declared a thin slice of U-3's
 * `BlobStore {put, appendChunk, get, delete, list}`. **U-3 has now built the real one at
 * `data/blobStore.ts`, and this module is SUPERSEDED for every product purpose — not absorbed.**
 *
 * It is deliberately still here, and still separate, for one reason: it writes the *harness*
 * database's own `audioChunks` / `audioSegments` stores (v002) and keeps per-chunk timing records
 * that exist to settle assumption A-2. The product's blob store keeps none of that. Merging them
 * would put measurement instrumentation on the path AUD-3 depends on, and would give the harness a
 * reason to touch the database holding real scouting data.
 *
 * **Nothing in the product may import this module.** `data/blobStore.ts` is the only blob path;
 * U-11's recorder consumes that one. When U-0 and U-1 are retired, this file goes with them.
 *
 * ── The two properties that make this worth having ───────────────────────────
 *
 * 1. **Chunks are persisted as they arrive, each in its own transaction** (design §8.2 step 2).
 *    AUD-3 requires that captured audio is never discarded; §8.5 makes the honest limit explicit —
 *    on a hard jetsam kill with no `pagehide`, everything written so far survives and at most one
 *    `TIMESLICE_MS` is lost. That is only true if each chunk is durable on arrival.
 *
 * 2. **Assembly is verbatim and by arrival index.** AUD-1 forbids transcoding on any path, so
 *    assembly is a `new Blob([...])` over the raw chunk blobs and nothing else — no re-muxing, no
 *    header rewriting, no codec touch. The order comes from the recorded `index`, never from the
 *    order the writes happened to complete, because chunk writes are async and can land out of
 *    order while A-2 is a claim about *arrival* order.
 */

const CHUNKS_STORE = 'audioChunks';
const SEGMENTS_STORE = 'audioSegments';

/**
 * Writes one `dataavailable` payload.
 *
 * Upsert by `[segmentId+index]`, so a re-run of the recovery pass cannot duplicate a chunk.
 *
 * @throws {StorageError} on any write failure. Surfaced, never swallowed (NFR-8) — a silently
 *   dropped chunk is a hole in the middle of a recording that nothing downstream can detect.
 */
export async function appendChunk(row: AudioChunkRow): Promise<void> {
  const handle = db();
  try {
    await handle.audioChunks.put(row);
  } catch (cause) {
    throw new StorageError(
      `Could not persist audio chunk ${row.index} of segment ${row.segmentId}. The recording now has a gap.`,
      { cause, context: { store: CHUNKS_STORE, segmentId: row.segmentId, index: row.index } },
    );
  }
}

/** Every chunk of a segment, in **arrival index** order. @throws {StorageError} */
export async function listChunks(segmentId: string): Promise<AudioChunkRow[]> {
  const handle = db();
  try {
    const rows = await handle.audioChunks.where('segmentId').equals(segmentId).toArray();
    return rows.sort((a, b) => a.index - b.index);
  } catch (cause) {
    throw new StorageError('Could not read the segment’s audio chunks.', {
      cause,
      context: { store: CHUNKS_STORE, segmentId },
    });
  }
}

/**
 * Concatenates the selected chunks verbatim.
 *
 * This is the operation assumption A-2 is *about*. There is deliberately nothing here but a `Blob`
 * constructor over the raw parts: AUD-1 says "playback uses the stored blob verbatim, with no
 * transcoding on any path", so if a fragmented-MP4 sequence needs fixing up to play, the correct
 * finding is that A-2 is false — not a fix-up step added here.
 *
 * @param indices arrival indices to include, in the order they should be concatenated. Indices with
 *   no matching chunk are skipped and reflected in the returned `chunkCount`.
 */
export function concatChunks(
  chunks: readonly AudioChunkRow[],
  indices: readonly number[],
  mime: string,
): { blob: Blob; chunkCount: number; sizeBytes: number } {
  const byIndex = new Map(chunks.map((c) => [c.index, c]));
  const parts: Blob[] = [];
  let sizeBytes = 0;
  for (const i of indices) {
    const chunk = byIndex.get(i);
    if (!chunk) continue;
    parts.push(chunk.blob);
    sizeBytes += chunk.sizeBytes;
  }
  return { blob: new Blob(parts, { type: mime }), chunkCount: parts.length, sizeBytes };
}

/** @throws {StorageError} */
export async function putSegment(row: AudioSegmentRow): Promise<void> {
  const handle = db();
  try {
    await handle.audioSegments.put(row);
  } catch (cause) {
    throw new StorageError(
      'Could not persist the audio segment. Export the run before leaving this screen.',
      { cause, context: { store: SEGMENTS_STORE, id: row.id } },
    );
  }
}

/** @throws {StorageError} */
export async function getSegment(id: string): Promise<AudioSegmentRow | undefined> {
  const handle = db();
  try {
    return await handle.audioSegments.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the audio segment.', {
      cause,
      context: { store: SEGMENTS_STORE, id },
    });
  }
}

/** Newest first. @throws {StorageError} */
export async function listSegments(): Promise<AudioSegmentRow[]> {
  const handle = db();
  try {
    return await handle.audioSegments.orderBy('startedAt').reverse().toArray();
  } catch (cause) {
    throw new StorageError('Could not list audio segments.', { cause });
  }
}

/**
 * Segments whose finalize never ran — design §8.5's `pageshow` recovery input.
 *
 * Filtered in memory rather than by index: IndexedDB does not accept a boolean as a key, so an
 * index on `complete` would silently hold no entries at all. The store is capped at
 * `AUDIO_SEGMENT_HISTORY_CAP`, so this scans a dozen rows.
 *
 * @throws {StorageError}
 */
export async function listIncompleteSegments(): Promise<AudioSegmentRow[]> {
  return (await listSegments()).filter((s) => !s.complete);
}

/**
 * Deletes a segment and its chunks in **one transaction** — the same all-or-nothing rule INV-8
 * states for the product's cascade. A half-deleted segment would leave orphan chunks that the next
 * recovery pass would try to assemble into a segment row that no longer exists.
 *
 * @throws {StorageError}
 */
export async function deleteSegment(id: string): Promise<void> {
  const handle = db();
  try {
    await handle.transaction('rw', handle.audioSegments, handle.audioChunks, async () => {
      await handle.audioSegments.delete(id);
      await handle.audioChunks.where('segmentId').equals(id).delete();
    });
  } catch (cause) {
    throw new StorageError('Could not delete the audio segment and its chunks.', {
      cause,
      context: { id },
    });
  }
}

/**
 * Sweeps the oldest segments past `AUDIO_SEGMENT_HISTORY_CAP`.
 *
 * Incomplete segments are **never** swept: they are the AUD-3 evidence — the thing that proves what
 * survived an interruption — and a cap is not a reason to destroy the only copy of that.
 *
 * @returns the ids removed.
 * @throws {StorageError}
 */
export async function trimSegments(): Promise<string[]> {
  const all = await listSegments();
  const complete = all.filter((s) => s.complete);
  const excess = complete.length - config.AUDIO_SEGMENT_HISTORY_CAP;
  if (excess <= 0) return [];

  // `listSegments` is newest-first, so the tail holds the oldest — reversed so the returned list
  // (and the log line it feeds) reads oldest-first, which is the order a reader expects from
  // "swept the oldest".
  const doomed = complete
    .slice(complete.length - excess)
    .map((s) => s.id)
    .reverse();
  for (const id of doomed) await deleteSegment(id);
  return doomed;
}

/** Removes every spike segment and chunk, so a venue run leaves no audio on the phone. */
export async function clearAudioSpike(): Promise<void> {
  const handle = db();
  try {
    await handle.transaction('rw', handle.audioSegments, handle.audioChunks, async () => {
      await handle.audioSegments.clear();
      await handle.audioChunks.clear();
    });
  } catch (cause) {
    throw new StorageError('Could not clear the audio spike stores.', { cause });
  }
}
